import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Building2,
  Cake,
  Gift,
  Heart,
  IndianRupee,
  MessageCircle,
  Repeat,
  ShoppingBag,
  Sparkles,
  UserPlus,
  Users,
  UserCheck,
} from "lucide-react";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { RefreshBar } from "@/components/admin/RefreshBar";
import {
  AnalyticsExportButtons,
  AnalyticsFilters,
  ChartFrame,
  DataTable,
  KpiCard,
  MetricBars,
  Panel,
  PIE_TONES,
  TONE_VAR,
  compactInr,
  inr,
  num,
  pct0,
  tooltipStyle,
} from "@/components/admin/analytics/AnalyticsKit";
import { getCustomerAnalytics } from "@/lib/admin/analytics-suite.functions";
import type { SuiteRangeKey } from "@/lib/admin/analytics-range";
import type { CustomerAnalytics } from "@/lib/admin/customer-analytics.data";

export const Route = createFileRoute("/_admin/admin/customer-analytics")({
  component: CustomerAnalyticsPage,
});

function CustomerAnalyticsPage() {
  const [range, setRange] = useState<SuiteRangeKey>("30d");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const fetchCustomers = useServerFn(getCustomerAnalytics);

  const q = useQuery({
    queryKey: ["admin", "customer-analytics", range, from, to],
    queryFn: () =>
      fetchCustomers({ data: { range, ...(range === "custom" ? { from, to } : {}) } }),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

  const d = q.data as CustomerAnalytics | undefined;
  const loading = q.isLoading;
  const k = d?.kpis;
  const unverified = new Set(
    (d as { unverifiedKpis?: string[] } | undefined)?.unverifiedKpis ?? [],
  );
  const nm = (key: string, val: string) => (unverified.has(key) ? "Not measured" : val);

  const exportRows = useMemo(() =>
    (d?.topCustomers ?? []).map((c) => ({
      Customer: c.name,
      Area: c.area,
      Segment: c.segment,
      Orders: c.orders,
      "Revenue (₹)": c.revenue,
      "Lifetime value (₹)": c.ltv,
      "Last order": c.lastOrder,
    })), [d?.topCustomers]);

  const { onExportCsv, onExportExcel } = AnalyticsExportButtons({
    name: "customer-analytics",
    rows: exportRows,
  });

  return (
    <div>
      <AdminPageHeader
        title="Customer Analytics"
        description="Growth, retention, segments, subscriptions and satisfaction across the customer base."
      />

      <RefreshBar
        targets={[{ key: "customer-analytics", label: "customers" }]}
        busy={q.isFetching}
        lastUpdated={q.dataUpdatedAt}
      />

      <AnalyticsFilters
        range={range}
        onRangeChange={setRange}
        from={from}
        to={to}
        onFromChange={setFrom}
        onToChange={setTo}
        status={d ? `${d.rangeLabel} · ${(d as { source?: string }).source === "live" ? "Live" : "Not measured"}` : "Loading…"}
        onExportCsv={onExportCsv}
        onExportExcel={onExportExcel}
      />

      {(d as { source?: string } | undefined)?.source !== "live" ? (
        <div role="status" className="rounded-lg border border-border bg-muted p-6 text-sm text-muted-foreground">
          {q.isError ? "Unavailable — the analytics lookup failed." : "Not measured — Customer Analytics is not yet connected to verified live production data. No figures are shown to avoid displaying demo or misleading values."}
        </div>
      ) : null}
      {(d as { source?: string } | undefined)?.source === "live" ? (<>

      <section aria-label="Customer key metrics" className="mb-6">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-6">
          <KpiCard
            label="New customers"
            value={num(k?.newCustomers ?? 0)}
            tone="info"
            icon={UserPlus}
            delta={k?.deltas.newCustomers}
            loading={loading}
            to="/admin/customers"
          />
          <KpiCard
            label="Returning customers"
            value={num(k?.returningCustomers ?? 0)}
            tone="teal"
            icon={Repeat}
            delta={k?.deltas.returningCustomers}
            loading={loading}
            to="/admin/customers"
          />
          <KpiCard
            label="Active customers"
            value={num(k?.activeCustomers ?? 0)}
            tone="info"
            icon={Users}
            delta={k?.deltas.activeCustomers}
            loading={loading}
            to="/admin/customers"
          />
          <KpiCard
            label="Lifetime value"
            value={inr(k?.lifetimeValue ?? 0)}
            tone="purple"
            icon={IndianRupee}
            delta={k?.deltas.lifetimeValue}
            loading={loading}
          />
          <KpiCard
            label="Average spend"
            value={inr(k?.avgSpend ?? 0)}
            tone="success"
            icon={ShoppingBag}
            delta={k?.deltas.avgSpend}
            loading={loading}
            to="/admin/orders"
          />
          <KpiCard
            label="Average orders"
            value={String(k?.avgOrders ?? 0)}
            tone="neutral"
            icon={ShoppingBag}
            delta={k?.deltas.avgOrders}
            loading={loading}
          />
          <KpiCard
            label="Customer retention"
            value={nm("retention", pct0(k?.retention ?? 0))}
            tone="success"
            icon={UserCheck}
            delta={k?.deltas.retention}
            loading={loading}
          />
          <KpiCard
            label="Repeat purchase"
            value={nm("repeatPurchase", pct0(k?.repeatPurchase ?? 0))}
            tone="teal"
            icon={Repeat}
            delta={k?.deltas.repeatPurchase}
            loading={loading}
          />
          <KpiCard
            label="Referral customers"
            value={nm("referralCustomers", num(k?.referralCustomers ?? 0))}
            tone="warn"
            icon={Gift}
            delta={k?.deltas.referralCustomers}
            loading={loading}
          />
          <KpiCard
            label="Subscriptions"
            value={nm("subscriptions", num(k?.subscriptions ?? 0))}
            tone="purple"
            icon={Sparkles}
            delta={k?.deltas.subscriptions}
            loading={loading}
            to="/admin/meal-plans-v2"
          />
          <KpiCard
            label="Corporate clients"
            value={nm("corporateClients", num(k?.corporateClients ?? 0))}
            tone="neutral"
            icon={Building2}
            delta={k?.deltas.corporateClients}
            loading={loading}
          />
          <KpiCard
            label="WhatsApp customers"
            value={nm("whatsappCustomers", num(k?.whatsappCustomers ?? 0))}
            tone="success"
            icon={MessageCircle}
            delta={k?.deltas.whatsappCustomers}
            loading={loading}
            to="/admin/whatsapp-templates"
          />
        </div>
      </section>

      <div className="mb-6 grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Panel title="Customer growth" subtitle="New vs returning per day" className="xl:col-span-2">
          <ChartFrame loading={loading} empty={(d?.growth.length ?? 0) === 0}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={d?.growth ?? []}>
                <defs>
                  <linearGradient id="newFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={TONE_VAR.info} stopOpacity={0.45} />
                    <stop offset="100%" stopColor={TONE_VAR.info} stopOpacity={0.03} />
                  </linearGradient>
                  <linearGradient id="retFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={TONE_VAR.teal} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={TONE_VAR.teal} stopOpacity={0.03} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="var(--color-muted-foreground)" />
                <YAxis tick={{ fontSize: 11 }} stroke="var(--color-muted-foreground)" />
                <Tooltip {...tooltipStyle()} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area
                  type="monotone"
                  dataKey="newCustomers"
                  name="New"
                  stroke={TONE_VAR.info}
                  strokeWidth={2}
                  fill="url(#newFill)"
                />
                <Area
                  type="monotone"
                  dataKey="returning"
                  name="Returning"
                  stroke={TONE_VAR.teal}
                  strokeWidth={2}
                  fill="url(#retFill)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartFrame>
        </Panel>

        <Panel title="New vs returning" subtitle="Share of active customers">
          <ChartFrame loading={loading} empty={(d?.newVsReturning.length ?? 0) === 0}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={d?.newVsReturning ?? []}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={50}
                  outerRadius={85}
                  paddingAngle={2}
                >
                  <Cell fill={TONE_VAR.info} />
                  <Cell fill={TONE_VAR.teal} />
                </Pie>
                <Tooltip {...tooltipStyle()} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartFrame>
        </Panel>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Panel title="Customer lifetime value" subtitle="Customers per LTV bucket">
          <ChartFrame loading={loading} empty={(d?.ltvBuckets.length ?? 0) === 0}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d?.ltvBuckets ?? []}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="var(--color-muted-foreground)" />
                <YAxis tick={{ fontSize: 11 }} stroke="var(--color-muted-foreground)" />
                <Tooltip {...tooltipStyle()} />
                <Bar dataKey="value" name="Customers" fill={TONE_VAR.info} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartFrame>
        </Panel>

        <Panel title="Customer segments" subtitle="VIP · Gold · Silver · Bronze · New · Inactive">
          <MetricBars rows={d?.segments ?? []} tone="purple" loading={loading} />
        </Panel>
      </div>

      <Panel title="Top customers" subtitle="Ranked by revenue" className="mb-6">
        <DataTable
          rows={d?.topCustomers ?? []}
          loading={loading}
          exportName="top-customers"
          searchOn={(r) => `${r.name} ${r.area} ${r.segment}`}
          columns={[
            {
              key: "name",
              header: "Customer",
              render: (r) => (
                <div className="flex items-center gap-2">
                  <span
                    className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-[10px] font-semibold"
                    style={{ background: "var(--ops-info-soft)", color: "var(--ops-info)" }}
                    aria-hidden
                  >
                    {r.name
                      .split(" ")
                      .map((p) => p[0])
                      .join("")
                      .slice(0, 2)}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-foreground">{r.name}</span>
                    <span className="block text-xs text-muted-foreground">
                      {r.area} · {r.segment}
                    </span>
                  </span>
                </div>
              ),
              sortValue: (r) => r.name,
              exportValue: (r) => r.name,
            },
            {
              key: "orders",
              header: "Orders",
              align: "right",
              render: (r) => num(r.orders),
              sortValue: (r) => r.orders,
              exportValue: (r) => r.orders,
            },
            {
              key: "revenue",
              header: "Revenue",
              align: "right",
              render: (r) => inr(r.revenue),
              sortValue: (r) => r.revenue,
              exportValue: (r) => r.revenue,
            },
            {
              key: "ltv",
              header: "Lifetime value",
              align: "right",
              render: (r) => inr(r.ltv),
              sortValue: (r) => r.ltv,
              exportValue: (r) => r.ltv,
            },
            {
              key: "last",
              header: "Last order",
              align: "right",
              render: (r) => r.lastOrder,
              sortValue: (r) => r.lastOrder,
              exportValue: (r) => r.lastOrder,
            },
          ]}
        />
      </Panel>

      <div className="mb-6 grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Panel title="Top cities">
          <MetricBars rows={d?.cities ?? []} tone="info" loading={loading} />
        </Panel>
        <Panel title="Top areas">
          <MetricBars rows={d?.areas ?? []} tone="teal" loading={loading} />
        </Panel>
        <Panel title="Customer acquisition source">
          <MetricBars rows={d?.acquisition ?? []} tone="purple" loading={loading} />
        </Panel>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Panel title="Subscription analytics" subtitle="Recurring plan health">
          <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {[
              ["Active", num(d?.subscription.active ?? 0)],
              ["Paused", num(d?.subscription.paused ?? 0)],
              ["Cancelled", num(d?.subscription.cancelled ?? 0)],
              ["MRR", compactInr(d?.subscription.mrr ?? 0)],
              ["Avg tenure", `${d?.subscription.avgTenureDays ?? 0}d`],
              ["Churn", pct0(d?.subscription.churn ?? 0)],
            ].map(([label, value]) => (
              <div key={label} className="rounded-lg border border-border p-3">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
                <p className="mt-1 text-lg font-semibold tabular-nums text-foreground">{value}</p>
              </div>
            ))}
          </div>
          <MetricBars rows={d?.subscription.byPlan ?? []} tone="purple" loading={loading} />
        </Panel>

        <Panel title="Corporate analytics" subtitle="B2B accounts">
          <div className="mb-3 grid grid-cols-3 gap-3">
            {[
              ["Accounts", num(d?.corporate.accounts ?? 0)],
              ["Revenue", compactInr(d?.corporate.revenue ?? 0)],
              ["Avg headcount", num(d?.corporate.avgHeadcount ?? 0)],
            ].map(([label, value]) => (
              <div key={label} className="rounded-lg border border-border p-3">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
                <p className="mt-1 text-lg font-semibold tabular-nums text-foreground">{value}</p>
              </div>
            ))}
          </div>
          <MetricBars rows={d?.corporate.topAccounts ?? []} tone="neutral" loading={loading} money />
        </Panel>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Panel title="Repeat purchase timeline" subtitle="Gap between orders">
          <MetricBars rows={d?.repeatTimeline ?? []} tone="teal" loading={loading} />
        </Panel>

        <Panel title="Customer retention curve" subtitle="Share still ordering">
          <ChartFrame loading={loading} empty={(d?.retentionCurve.length ?? 0) === 0}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={d?.retentionCurve ?? []}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="period" tick={{ fontSize: 11 }} stroke="var(--color-muted-foreground)" />
                <YAxis
                  tick={{ fontSize: 11 }}
                  stroke="var(--color-muted-foreground)"
                  tickFormatter={(v: number) => `${Math.round(v * 100)}%`}
                />
                <Tooltip {...tooltipStyle()} formatter={(v: number) => pct0(v)} />
                <Line
                  type="monotone"
                  dataKey="retained"
                  name="Retained"
                  stroke={TONE_VAR.info}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartFrame>
        </Panel>
      </div>

      <Panel title="RFM analysis" subtitle="Recency · Frequency · Monetary" className="mb-6">
        <DataTable
          rows={d?.rfm ?? []}
          loading={loading}
          exportName="rfm-analysis"
          searchOn={(r) => r.segment}
          minWidth={620}
          columns={[
            {
              key: "segment",
              header: "Segment",
              render: (r) => <span className="font-medium text-foreground">{r.segment}</span>,
              sortValue: (r) => r.segment,
              exportValue: (r) => r.segment,
            },
            {
              key: "customers",
              header: "Customers",
              align: "right",
              render: (r) => num(r.customers),
              sortValue: (r) => r.customers,
              exportValue: (r) => r.customers,
            },
            {
              key: "recency",
              header: "Recency (days)",
              align: "right",
              render: (r) => num(r.recencyDays),
              sortValue: (r) => r.recencyDays,
              exportValue: (r) => r.recencyDays,
            },
            {
              key: "frequency",
              header: "Frequency",
              align: "right",
              render: (r) => num(r.frequency),
              sortValue: (r) => r.frequency,
              exportValue: (r) => r.frequency,
            },
            {
              key: "monetary",
              header: "Monetary",
              align: "right",
              render: (r) => inr(r.monetary),
              sortValue: (r) => r.monetary,
              exportValue: (r) => r.monetary,
            },
          ]}
        />
      </Panel>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Panel title="Birthday calendar" subtitle="Upcoming birthdays">
          <ul className="space-y-2">
            {(d?.birthdays ?? []).map((b) => (
              <li
                key={b.name}
                className="flex items-center justify-between gap-2 rounded-md border border-border p-2 text-sm transition-colors hover:bg-muted/50"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <Cake className="h-4 w-4 shrink-0 text-ops-warn" aria-hidden />
                  <span className="truncate text-foreground">{b.name}</span>
                </span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {b.date} · {num(b.orders)} orders
                </span>
              </li>
            ))}
            {loading ? <li className="text-xs text-muted-foreground">Loading…</li> : null}
          </ul>
        </Panel>

        <Panel title="Customer loyalty score" subtitle="0–100 engagement index">
          <MetricBars rows={d?.loyalty ?? []} tone="success" loading={loading} />
        </Panel>

        <Panel title="Customer satisfaction" subtitle="Rating · NPS · reviews">
          <div className="mb-3 grid grid-cols-3 gap-3">
            {[
              ["Avg rating", `${d?.satisfaction.rating ?? 0} ★`],
              ["NPS", String(d?.satisfaction.nps ?? 0)],
              ["Reviews", num(d?.satisfaction.reviews ?? 0)],
            ].map(([label, value]) => (
              <div key={label} className="rounded-lg border border-border p-3">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
                <p className="mt-1 text-lg font-semibold tabular-nums text-foreground">{value}</p>
              </div>
            ))}
          </div>
          <MetricBars rows={d?.satisfaction.distribution ?? []} tone="warn" loading={loading} />
          <p className="mt-3 flex items-center gap-1 text-xs text-muted-foreground">
            <Heart className="h-3 w-3" aria-hidden /> Favourite meal per top customer:{" "}
            {(d?.favouriteMeals ?? [])
              .slice(0, 3)
              .map((f) => `${f.customer.split(" ")[0]} → ${f.meal}`)
              .join(", ") || "—"}
          </p>
        </Panel>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4">
        <Panel title="Most ordered meals per customer" subtitle="Top repeat pairings">
          <DataTable
            rows={d?.favouriteMeals ?? []}
            loading={loading}
            exportName="favourite-meals"
            searchOn={(r) => `${r.customer} ${r.meal}`}
            minWidth={480}
            columns={[
              {
                key: "customer",
                header: "Customer",
                render: (r) => <span className="font-medium text-foreground">{r.customer}</span>,
                sortValue: (r) => r.customer,
                exportValue: (r) => r.customer,
              },
              {
                key: "meal",
                header: "Meal",
                render: (r) => r.meal,
                sortValue: (r) => r.meal,
                exportValue: (r) => r.meal,
              },
              {
                key: "times",
                header: "Times ordered",
                align: "right",
                render: (r) => num(r.times),
                sortValue: (r) => r.times,
                exportValue: (r) => r.times,
              },
            ]}
          />
        </Panel>
      </div>
      </>) : null}
    </div>
  );
}
